// src/components/WhyChooseUs.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Truck, Shield, Heart, Zap, Award, Users, TrendingUp, Lock,
  Leaf, Gift, Bell, RefreshCw, CheckCircle, Clock, ThumbsUp,
  Package, Star, Home, Smartphone, DollarSign
} from 'lucide-react';

const WhyChooseUs = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'why-choose-us');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching Why Choose Us:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  if (loading || !config || config.active === false) return null;

  const features = config.features || [];
  if (features.length === 0) return null;

  const renderIcon = (iconName, size = 28) => {
    const iconProps = { size, style: { color: 'var(--color-link)' } };
    switch (iconName) {
      case 'truck': return <Truck {...iconProps} />;
      case 'shield': return <Shield {...iconProps} />;
      case 'heart': return <Heart {...iconProps} />;
      case 'zap': return <Zap {...iconProps} />;
      case 'award': return <Award {...iconProps} />;
      case 'users': return <Users {...iconProps} />;
      case 'trending': return <TrendingUp {...iconProps} />;
      case 'lock': return <Lock {...iconProps} />;
      case 'leaf': return <Leaf {...iconProps} />;
      case 'gift': return <Gift {...iconProps} />;
      case 'bell': return <Bell {...iconProps} />;
      case 'refresh': return <RefreshCw {...iconProps} />;
      case 'clock': return <Clock {...iconProps} />;
      case 'thumbsup': return <ThumbsUp {...iconProps} />;
      case 'package': return <Package {...iconProps} />;
      case 'star': return <Star {...iconProps} />;
      case 'home': return <Home {...iconProps} />;
      case 'phone': return <Smartphone {...iconProps} />;
      case 'dollar': return <DollarSign {...iconProps} />;
      default: return <CheckCircle {...iconProps} />;
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16 anim-fadeInUp">

      {/* Header */}
      <div className="text-center mb-8 md:mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
          style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)', color: 'var(--color-link)' }}>
          <Star size={14} className="fill-current" />
          <span className="text-xs font-bold uppercase tracking-wider">Why Choose Us</span>
        </div>
        <h2 className="text-2xl md:text-4xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
          {config.title || 'Why Choose Organic Land?'}
        </h2>
        <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: 'var(--color-secondary-text)' }}>
          {config.subtitle || 'Hum kyun best hain aapke liye'}
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {features.map((feature, i) => (
          <div
            key={i}
            className="relative rounded-2xl p-6 text-center card-lift group anim-fadeInUp overflow-hidden"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              border: `1px solid var(--color-border)`,
              animationDelay: `${i * 100}ms`,
            }}
          >
            {/* Hover gradient effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{ background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.05) 0%, transparent 100%)' }}></div>

            <div className="relative z-10">
              {/* Icon */}
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                {renderIcon(feature.icon)}
              </div>

              {/* Number Badge */}
              <div className="absolute top-4 right-4 text-4xl font-bold opacity-5"
                style={{ color: 'var(--color-link)' }}>
                {String(i + 1).padStart(2, '0')}
              </div>

              {/* Title */}
              <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-primary-text)' }}>
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-secondary-text)' }}>
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>

    </section>
  );
};

export default WhyChooseUs;